import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 51cg吃瓜网`;
    } else {
      document.title = '文章不存在 - 51cg吃瓜网';
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-2xl text-foreground-500"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">文章不存在</h1>
            <p className="text-foreground-600 mb-6">该文章可能已被删除或链接无效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article.slug, article.category);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    publisher: {
      '@type': 'Organization',
      name: '51cg吃瓜网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://51cg.fun/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://51cg.fun' },
      { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://51cg.fun/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <Navbar />
      <main className="pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <div className="flex flex-col lg:flex-row gap-8 mt-6">
            <article className="flex-1 max-w-full lg:max-w-[calc(100%-340px)]">
              <div className="mb-6">
                <span className="inline-block px-3 py-1 bg-primary-50 text-primary-500 text-xs font-medium rounded-full mb-4 font-label">
                  {article.categoryName}
                </span>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-950 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-600">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line text-xs"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line text-xs"></i>
                    {article.publishedAt}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line text-xs"></i>
                    {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line text-xs"></i>
                    {article.comments} 评论
                  </span>
                </div>
              </div>

              <div className="rounded-xl overflow-hidden mb-8 bg-background-200">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-auto object-cover max-h-[450px]"
                />
              </div>

              <div
                className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200/70">
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category/${article.category}`}
                    className="px-3 py-1.5 bg-background-100 text-foreground-600 text-xs rounded-full hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </article>

            <aside className="w-full lg:w-80 flex-shrink-0">
              <div className="lg:sticky lg:top-24 space-y-6">
                <div className="bg-background-100 rounded-xl p-5">
                  <h3 className="text-base font-bold font-heading text-foreground-950 mb-2">
                    关于作者
                  </h3>
                  <p className="text-sm text-foreground-600">
                    {article.author} · 51cg吃瓜网特约作者，专注娱乐八卦与热点资讯领域。
                  </p>
                </div>

                {relatedArticles.length > 0 && (
                  <div className="bg-background-100 rounded-xl p-5">
                    <h3 className="text-base font-bold font-heading text-foreground-950 mb-4">
                      相关推荐
                    </h3>
                    <div className="space-y-1">
                      {relatedArticles.map((rel) => (
                        <ArticleCard key={rel.id} article={rel} variant="horizontal" />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}